import { HttpClient, HttpResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { environment } from '../../../environments/environment';
import { ApiEndpoints } from '../../core/api-endpoints.service';
import { IdService } from '../../core/id.service';
import { StorageService } from '../../core/storage.service';
import {
  AccessRequest,
  DataRecord,
  DEFAULT_LOCKED_SPACE,
  LockedSpace,
  WorkflowState,
} from '../../core/workflow-state.service';
import { ApiCallState } from '../../shared/api-call-state';
import {
  ApiEndpointComponent,
  ApiResultComponent,
} from '../../shared/api-result.component';
import {
  dateInputValue,
  epochSecondsFromNow,
  mergeDateAndTime,
} from '../../shared/date-utils';

interface BookingCache {
  systemProvider?: string;
  lockId?: string;
}

interface BookingForm {
  mapId: string;
  lockedSpaceId: string;
  email: string;
  phone: string;
  systemProvider: string;
  itemId: string;
  lockId: string;
  length: number;
  width: number;
  height: number;
  country: string;
  boxType: string;
  bookingPreference: string;
  accessType: string;
  dropoffTimeLimit: number;
  deliveryType: string;
  requesterId: string;
  shipperName: string;
  streetAddress: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  latitude: number;
  longitude: number;
  deliveryDateFrom: string;
  deliveryTimeFrom: string;
  deliveryDateTo: string;
  deliveryTimeTo: string;
  description: string;
  recipientResponseDate: string;
  recipientResponseTime: string;
  createEdi: boolean;
}

interface EdiResponse {
  idInformation?: Array<{ ids?: Array<{ value?: string }> }>;
}

@Component({
  selector: 'app-access-booking',
  imports: [FormsModule, ApiEndpointComponent, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './access-booking.component.html',
})
export class AccessBookingComponent implements OnInit {
  readonly directBooking = input(false);

  private readonly http = inject(HttpClient);
  private readonly endpoints = inject(ApiEndpoints);
  private readonly ids = inject(IdService);
  private readonly storage = inject(StorageService);
  private readonly workflow = inject(WorkflowState);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly endpoint = this.endpoints.url(this.endpoints.accessRequest);
  protected readonly call = new ApiCallState();
  protected readonly creatingEdi = signal(false);
  protected readonly previousItemId = signal('-');
  protected readonly requestId = signal('');
  protected readonly lockedSpaceOptions = computed(() =>
    Array.from(this.workflow.lockedSpaces(), ([id, value]) => ({
      id,
      label: `${String(value['displayName'] ?? value['lockId'] ?? id)} | ${String(value['email'] ?? '')}`,
    })),
  );

  protected readonly model: BookingForm = createDefaultBookingForm();

  ngOnInit(): void {
    if (this.directBooking()) {
      const cache = this.storage.get<BookingCache>('accessRequestCache', {});
      Object.assign(this.model, {
        systemProvider: cache.systemProvider ?? '',
        lockId: cache.lockId ?? '1526010',
        lockedSpaceId: '-',
        boxType: '2',
        country: 'SE',
        postalCode: '11122',
        city: 'Stockholm',
      });
    }
  }

  protected selectLockedSpace(): void {
    const selected = this.workflow.lockedSpaces().get(this.model.mapId);
    if (!selected) {
      return;
    }

    applyLockedSpace(this.model, selected);
    this.call.clear();
  }

  protected toggleEdi(enabled: boolean): void {
    this.model.createEdi = enabled;
    if (!enabled) {
      this.model.itemId = newItemId();
      return;
    }

    const partyId = this.model.lockId.trim() || '2024444';
    const payload = createEdiPayload(partyId);
    this.creatingEdi.set(true);

    this.http
      .post<EdiResponse>(environment.createEdiUrl, payload, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'x-api-key': atob('ZktlS3pXSk5DTzg3RnNpWXd6TlF2MXRiV2JGcm9UUW42SUVvQ0pzQg=='),
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const itemId = response.idInformation?.[0]?.ids?.[0]?.value;
          if (itemId) {
            this.model.itemId = itemId;
            this.model.lockId = partyId;
          } else {
            this.model.createEdi = false;
          }
          this.creatingEdi.set(false);
        },
        error: (error: unknown) => {
          this.model.createEdi = false;
          this.creatingEdi.set(false);
          this.call.fail(error);
        },
      });
  }

  protected submit(): void {
    const itemId = this.model.itemId;
    const deliveryFrom = mergeDateAndTime(
      this.model.deliveryDateFrom,
      this.model.deliveryTimeFrom,
    );
    const deliveryTo = mergeDateAndTime(this.model.deliveryDateTo, this.model.deliveryTimeTo);
    const recipientResponse = mergeDateAndTime(
      this.model.recipientResponseDate,
      this.model.recipientResponseTime,
    );
    const requestId = this.ids.shortId();
    const deliveryAddress: DataRecord = {
      streetAddress: this.model.streetAddress,
      streetNumber: this.model.streetNumber,
      postalCode: this.model.postalCode,
      city: this.model.city,
      country: this.model.country,
      latitude: this.model.latitude,
      longitude: this.model.longitude,
    };

    if (this.model.bookingPreference) {
      deliveryAddress['bookingPreference'] = { position: this.model.bookingPreference };
    }

    const payload: DataRecord = {
      lockedSpaceId: this.model.lockedSpaceId,
      ttl: epochSecondsFromNow(245),
      requestId,
      deliveryInstruction: {
        lockOwnerId: {
          emailAddress: this.model.email || undefined,
          phoneNumber: this.model.phone || undefined,
        },
        deliveryAddress,
        deliveryItem: {
          id: itemId,
          description: this.model.description,
          dimension: {
            length: this.model.length,
            width: this.model.width,
            height: this.model.height,
          },
          weight: { weight: 0 },
          characteristics: {
            temperatureControl: { rangeMinimum: 0, rangeMaximum: 0 },
            misc: 'string',
          },
        },
      },
      requestor: {
        shipperName: this.model.shipperName,
        requesterId: this.model.requesterId,
      },
      deliveryTime: {
        from: deliveryFrom.toISOString(),
        to: deliveryTo.toISOString(),
      },
      systemProvider: this.model.systemProvider,
      accessType: this.model.accessType,
      boxType: this.model.boxType,
      lockId: this.model.lockId,
      deliveryType: this.model.accessType === 'DELIVERY' ? this.model.deliveryType : undefined,
      recipientResponseTimeLimit:
        this.model.systemProvider === 'AssaAbloy' ? recipientResponse.toISOString() : undefined,
    };

    if (this.model.accessType === 'DROPOFF') {
      payload['dropoffTimeLimit'] = this.model.dropoffTimeLimit;
    }

    this.storage.set<BookingCache>('accessRequestCache', {
      systemProvider: this.model.systemProvider,
      lockId: this.model.lockId,
    });
    this.call.start(payload);
    this.requestId.set('');

    this.http
      .post<DataRecord>(this.endpoint, payload, { observe: 'response' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => this.handleResponse(response, payload, itemId, requestId),
        error: (error: unknown) => this.call.fail(error),
      });
  }

  private handleResponse(
    response: HttpResponse<DataRecord>,
    payload: DataRecord,
    itemId: string,
    requestId: string,
  ): void {
    this.previousItemId.set(itemId);
    this.model.createEdi = false;

    if (response.status === 202) {
      const mapId = this.directBooking() ? itemId : this.model.mapId;
      const selected = this.workflow.lockedSpaces().get(this.model.mapId);
      this.workflow.setPendingAccessRequest(mapId, {
        ...payload,
        mapId,
        displayName: selected?.['displayName'] ?? itemId,
        systemProvider: selected?.['systemProvider'] ?? this.model.systemProvider,
        itemId,
      });
      this.requestId.set(requestId);
      this.call.succeed({ status: 202, requestId });
      this.model.itemId = newItemId();
      return;
    }

    const mapId = this.directBooking() ? itemId : this.model.mapId;
    const body = response.body ?? {};
    const data: AccessRequest = {
      ...body,
      mapId,
      systemProvider: this.model.systemProvider,
      itemId,
    };

    const forwardUrl = body['forwardUrl'];
    if (
      this.model.systemProvider.toLowerCase() === 'volvo' &&
      typeof forwardUrl === 'string'
    ) {
      window.open(forwardUrl, '_blank', 'noopener,noreferrer');
    }

    this.workflow.updateLockedSpace(mapId, data);
    this.workflow.addAccessRequest(data);
    this.call.succeed(data);
    this.requestId.set('');
    this.model.itemId = newItemId();
  }
}

function createDefaultBookingForm(): BookingForm {
  const from = new Date();
  const to = new Date(from);
  to.setDate(to.getDate() + 3);

  return {
    mapId: '',
    lockedSpaceId: '-',
    email: String(DEFAULT_LOCKED_SPACE['email'] ?? ''),
    phone: String(DEFAULT_LOCKED_SPACE['phoneNumber'] ?? ''),
    systemProvider: '',
    itemId: newItemId(),
    lockId: '',
    length: Number(DEFAULT_LOCKED_SPACE['length'] ?? 10),
    width: Number(DEFAULT_LOCKED_SPACE['width'] ?? 5),
    height: Number(DEFAULT_LOCKED_SPACE['height'] ?? 7),
    country: 'DK',
    boxType: '',
    bookingPreference: '',
    accessType: 'DELIVERY',
    dropoffTimeLimit: 24,
    deliveryType: 'HomeDelivery',
    requesterId: String(DEFAULT_LOCKED_SPACE['requesterId'] ?? ''),
    shipperName: String(DEFAULT_LOCKED_SPACE['shipperName'] ?? ''),
    streetAddress: String(DEFAULT_LOCKED_SPACE['streetAddress'] ?? ''),
    streetNumber: String(DEFAULT_LOCKED_SPACE['streetNumber'] ?? ''),
    postalCode: String(DEFAULT_LOCKED_SPACE['postalCode'] ?? ''),
    city: String(DEFAULT_LOCKED_SPACE['city'] ?? ''),
    latitude: Number(DEFAULT_LOCKED_SPACE['latitude'] ?? 0),
    longitude: Number(DEFAULT_LOCKED_SPACE['longitude'] ?? 0),
    deliveryDateFrom: dateInputValue(from),
    deliveryTimeFrom: '',
    deliveryDateTo: dateInputValue(to),
    deliveryTimeTo: '',
    description: '',
    recipientResponseDate: dateInputValue(from),
    recipientResponseTime: '',
    createEdi: false,
  };
}

function applyLockedSpace(model: BookingForm, value: LockedSpace): void {
  model.lockedSpaceId = String(value['lockedSpaceId'] ?? '');
  model.email = String(value['email'] ?? '');
  model.phone = String(value['phoneNumber'] ?? value['phone'] ?? '');
  model.systemProvider = String(value['systemProvider'] ?? '');
  model.lockId = String(value['lockId'] ?? '');
  model.length = Number(value['length'] ?? model.length);
  model.width = Number(value['width'] ?? model.width);
  model.height = Number(value['height'] ?? model.height);
  model.country = String(value['country'] ?? model.country);
  model.requesterId = String(value['requesterId'] ?? model.requesterId);
  model.shipperName = String(value['shipperName'] ?? model.shipperName);
  model.streetAddress = String(value['streetAddress'] ?? model.streetAddress);
  model.streetNumber = String(value['streetNumber'] ?? model.streetNumber);
  model.postalCode = String(value['postalCode'] ?? model.postalCode);
  model.city = String(value['city'] ?? model.city);
  model.latitude = Number(value['latitude'] ?? model.latitude);
  model.longitude = Number(value['longitude'] ?? model.longitude);
}

function newItemId(): string {
  return `SESAM${Date.now()}`;
}

function createEdiPayload(partyId: string): DataRecord {
  const timestamp = new Date().toISOString();
  return {
    messageDate: timestamp,
    messageFunction: 'Instruction',
    messageId: crypto.randomUUID(),
    application: {
      applicationId: 1438,
      name: 'PostNord Online Shipping Tool',
      version: '1.0',
    },
    language: 'EN',
    updateIndicator: 'Original',
    shipment: [
      {
        shipmentIdentification: { shipmentId: '0' },
        dateAndTimes: { loadingDate: timestamp },
        service: { basicServiceCode: '19', additionalServiceCode: ['A4'] },
        freeText: [{ usageCode: 'DEL', text: 'Test' }],
        numberOfPackages: { value: 1 },
        totalGrossWeight: { value: 2.2, unit: 'KGM' },
        totalVolume: { value: 0.009, unit: 'MTQ' },
        parties: {
          deliveryParty: {
            partyIdentification: { partyId, partyIdType: '156' },
            party: {
              nameIdentification: { name: 'Paketbox Mybox test - Demo Client' },
              address: {
                streets: ['Terminalvagen 24'],
                postalCode: '17173',
                city: 'SOLNA',
                countryCode: 'SE',
              },
            },
          },
          consignor: {
            issuerCode: 'Z12',
            partyIdentification: { partyId: '0205958358', partyIdType: '160' },
            party: {
              nameIdentification: { name: 'Sender Name' },
              address: {
                streets: ['Terminalvagen 24'],
                postalCode: '17173',
                city: 'SOLNA',
                countryCode: 'SE',
              },
              contact: {
                contactName: 'Sender Contact',
                emailAddress: 'sender.contact@somewhere.com',
              },
            },
          },
          consignee: {
            party: {
              nameIdentification: { name: 'Receiver Name' },
              address: {
                streets: ['Terminalvagen 24'],
                postalCode: '17173',
                city: 'SOLNA',
                countryCode: 'SE',
              },
              contact: {
                contactName: 'Receiver Contact',
                emailAddress: 'receiver.contact@somewhere.com',
              },
            },
          },
        },
        goodsItem: [
          {
            packageTypeCode: 'PC',
            items: [
              {
                itemIdentification: { itemId: '0', itemIdType: 'S10' },
                dimensions: {
                  height: { value: 4, unit: 'CMT' },
                  width: { value: 4, unit: 'CMT' },
                  length: { value: 5, unit: 'CMT' },
                },
                grossWeight: { value: 2.2, unit: 'KGM' },
              },
            ],
            goodsDescription: 'goodsDescription',
          },
        ],
      },
    ],
  };
}